import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideInfo, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { EmbedVariant, embedSnippet } from '../../embed-snippet';
import { CodeBlockComponent } from '../code-block/code-block.component';
import { EmbedPreviewComponent } from '../../../../embed-preview/embed-preview.component';

interface VariantOption {
  value: EmbedVariant;
  label: string;
  description: string;
  /** Where to paste the code, shown under the code block. */
  placement: string;
}

const VARIANTS: VariantOption[] = [
  {
    value: 'widget',
    label: 'Schwebender Button',
    description: 'Unten rechts auf der Seite, öffnet den Shop als Fenster.',
    placement:
      'Am besten im Footer oder im Bereich „Code am Ende des Body“ deines Website-Baukastens einfügen – dann erscheint der Button auf allen Seiten.',
  },
  {
    value: 'inline',
    label: 'Im Seiteninhalt',
    description: 'Der Shop erscheint direkt an einer Stelle deiner Seite.',
    placement:
      'Füge den Code genau dort ein, wo der Shop erscheinen soll, z. B. in einem HTML- oder Code-Block deiner Gutschein-Seite.',
  },
];

/**
 * Pick how the shop appears on the website, see it sketched, copy the snippet.
 * Script and element are one snippet: the module script may sit anywhere on
 * the page, so nothing has to go into the <head>.
 */
@Component({
  selector: 'app-embed-code',
  imports: [
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmCardImports,
    CodeBlockComponent,
    EmbedPreviewComponent,
  ],
  providers: [provideIcons({ lucideInfo, lucideTriangleAlert })],
  templateUrl: './embed-code.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Block, so the parent's space-y gap between the cards applies to this host too.
  host: { class: 'block' },
})
export class EmbedCodeComponent {
  readonly storeId = input.required<string>();
  readonly website = input<string | undefined>();
  readonly primaryColor = input<string | undefined>();
  readonly textColor = input<string | undefined>();
  readonly shopTitle = input<string | undefined>();
  /** The enabled voucher kinds, for the inline sketch. */
  readonly freeAmount = input(true);
  readonly fixedAmounts = input(false);
  readonly products = input(false);

  protected readonly variants = VARIANTS;
  protected readonly variant = signal<EmbedVariant>('widget');

  protected readonly selected = computed(
    () => VARIANTS.find((option) => option.value === this.variant()) ?? VARIANTS[0],
  );
  protected readonly lines = computed(() => embedSnippet(this.storeId(), this.variant()));
}
